import type { FieldInputADTValue } from '@lighthouse/core'
import React, { useCallback, useMemo } from 'react'

import { NO_FIELD_INPUT, numberReg } from '../../../constants'
import { isFileValue, isIdsValue, isNumberValue, isRichTextValue, isStringArray, isTextValue } from '../../../utils'
import { isFieldNumberValue } from '../../../utils/fieldInput'
import CalendarField from '../BaseField/CalendarField'
import CascadeField from '../BaseField/CascadeField'
import { CheckboxField } from '../BaseField/CheckboxField'
import DateField from '../BaseField/DateField'
import DepartmentField from '../BaseField/DepartmentField'
import EmptyField from '../BaseField/EmptyField'
import FileField from '../BaseField/FileField'
import NotesField from '../BaseField/NoteField'
import NumberField from '../BaseField/NumberField'
import PersonField from '../BaseField/PersonField'
import PhoneNumberField from '../BaseField/PhoneNumberField'
import RelativeSelectField from '../BaseField/RelativeSelectField'
import SelectField from '../BaseField/SelectField'
import SimpleTextField from '../BaseField/SimpleTextField'
import { SliderField } from '../BaseField/SliderField'
import TextField from '../BaseField/TextField'
import StyleContent from '../StyleContent'
import type { FieldProps } from '../types'

interface WebsiteFieldProps extends FieldProps {}

const WebsiteField: React.FunctionComponent<WebsiteFieldProps> = props => {
    const { value: propValue, config, onChange, onBlur, dataSource, isValid = false, record, mode = 'normal', error, onRenderLabel } = props
    const { canEdit, inputType, fieldPointer = '', required } = config
    const field = dataSource?.schema?.[fieldPointer]
    const isControlled = useMemo(() => propValue !== undefined, [propValue])
    const value = propValue

    const handleChange = useCallback(
        (fieldValue: FieldInputADTValue) => {
            const res = onChange?.(fieldValue)
            return res || false
        },
        [onChange]
    )

    const handleBlur = useCallback(
        (fieldValue: FieldInputADTValue) => {
            const res = onBlur?.(fieldValue)
            return res || false
        },
        [onBlur]
    )

    const handleCellChange = useCallback(
        (fieldValue: FieldInputADTValue) => {
            onChange?.(fieldValue)
        },
        [onChange]
    )

    const fieldProps = useMemo(
        () => ({
            isControlled,
            readOnly: mode === 'normal' ? !canEdit : !canEdit || (!fieldPointer && !NO_FIELD_INPUT.includes(inputType))
        }),
        [canEdit, fieldPointer, inputType, isControlled, mode]
    )

    return (
        <StyleContent
            field={field}
            inputType={inputType}
            isReadOnly={fieldProps.readOnly}
            error={error}
            config={config}
            onRenderLabel={onRenderLabel}
        >
            {useMemo(() => {
                const isEmpty = !fieldPointer || !field
                if (isEmpty && !NO_FIELD_INPUT.includes(inputType) && mode === 'datasource') {
                    return <EmptyField type={inputType} config={config} />
                }
                switch (inputType) {
                    case 'text': {
                        return (
                            <TextField
                                {...props}
                                {...fieldProps}
                                textConfig={config}
                                value={isTextValue(value) ? value : ''}
                                onBlur={handleBlur}
                                onChange={handleChange}
                            />
                        )
                    }
                    case 'phoneNumber': {
                        return (
                            <PhoneNumberField
                                {...props}
                                {...fieldProps}
                                phoneNumberConfig={config}
                                value={isTextValue(value) ? value : ''}
                                onBlur={handleBlur}
                                onChange={handleChange}
                            />
                        )
                    }
                    case 'url':
                    case 'email': {
                        return (
                            <SimpleTextField
                                {...props}
                                {...fieldProps}
                                value={isTextValue(value) ? value : ''}
                                onBlur={handleBlur}
                                onChange={handleChange}
                            />
                        )
                    }
                    case 'number': {
                        const numberValue = isFieldNumberValue(value) ? value : ''
                        const finallyValue = numberReg.test(numberValue.toString()) ? numberValue : ''
                        return (
                            <NumberField
                                {...props}
                                {...fieldProps}
                                value={finallyValue}
                                numberField={field?.type === 'number' ? field : undefined}
                                numberConfig={config}
                                onBlur={handleBlur}
                                onChange={handleChange}
                            />
                        )
                    }
                    case 'slider': {
                        const numberValue = isFieldNumberValue(value) ? value : ''
                        const finallyValue = numberReg.test(numberValue.toString()) ? numberValue : ''
                        return (
                            <SliderField
                                {...props}
                                {...fieldProps}
                                value={finallyValue}
                                sliderConfig={config}
                                onChange={handleCellChange}
                            />
                        )
                    }
                    case 'date': {
                        return (
                            <DateField
                                {...props}
                                {...fieldProps}
                                dateField={field}
                                value={isFieldNumberValue(value) ? value : undefined}
                                dateConfig={config}
                                onChange={handleCellChange}
                            />
                        )
                    }
                    case 'file': {
                        return (
                            <FileField
                                {...props}
                                {...fieldProps}
                                fileConfig={config}
                                fileField={field?.type === 'file' || field?.type === 'video' ? field : undefined}
                                value={isFileValue(value) ? value : undefined}
                                onChange={handleCellChange}
                            />
                        )
                    }
                    case 'notes': {
                        return (
                            <NotesField
                                {...props}
                                {...fieldProps}
                                key={record?.id}
                                value={isRichTextValue(value) ? value : undefined}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                        )
                    }
                    // case 'select': {
                    //     return (
                    //         <SelectField
                    //             {...props}
                    //             {...fieldProps}
                    //             field={field?.type === 'select' && field ? field : null}
                    //             value={isIdsValue(value) ? value : []}
                    //             selectConfig={config}
                    //         />
                    //     )
                    // }
                    case 'relativeSelect': {
                        if (field?.type === 'select' || field?.type === 'singleSelect') {
                            return (
                                <SelectField
                                    {...props}
                                    {...fieldProps}
                                    selectConfig={field.type === 'select' ? field.select : field.singleSelect}
                                    relativeSelectConfig={config}
                                    value={isIdsValue(value) ? value : []}
                                    onChange={handleCellChange}
                                />
                            )
                        }
                        if (field?.type === 'role') {
                            return (
                                <RelativeSelectField
                                    {...props}
                                    {...fieldProps}
                                    value={value?.toString()}
                                    relativeSelectConfig={config}
                                    onChange={v =>
                                        handleCellChange({
                                            ...v,
                                            value: isStringArray(v.value) ? v.value : typeof v.value === 'string' ? v.value.split(',') : []
                                        } as FieldInputADTValue)
                                    }
                                />
                            )
                        }
                        return (
                            <RelativeSelectField
                                {...props}
                                {...fieldProps}
                                value={isNumberValue(value) ? value.toString() : undefined}
                                relativeSelectConfig={config}
                                onChange={handleCellChange}
                            />
                        )
                    }
                    case 'person': {
                        return (
                            <PersonField
                                {...props}
                                {...fieldProps}
                                value={isIdsValue(value) ? value : []}
                                personField={field?.type === 'person' ? field : undefined}
                                personConfig={config}
                                onChange={handleCellChange}
                            />
                        )
                    }
                    case 'checkbox': {
                        return (
                            <CheckboxField
                                {...props}
                                {...fieldProps}
                                value={Boolean(value)}
                                checkBoxConfig={config}
                                onChange={handleCellChange}
                            />
                        )
                    }
                    case 'cascade': {
                        if (field?.type === 'department' || field?.type === 'parentDepartment') {
                            return (
                                <DepartmentField
                                    {...props}
                                    {...fieldProps}
                                    cascadeConfig={config}
                                    value={isStringArray(value) ? value : []}
                                    onChange={handleCellChange}
                                />
                            )
                        }
                        return (
                            <CascadeField
                                {...props}
                                {...fieldProps}
                                writeField={field}
                                cascadeConfig={config}
                                value={isTextValue(value) ? value : ''}
                                onChange={handleCellChange}
                            />
                        )
                    }
                    case 'calendar': {
                        return (
                            <CalendarField
                                {...props}
                                {...fieldProps}
                                calendarConfig={config}
                                calendarField={field}
                                value={isFieldNumberValue(value) ? value : ''}
                                onChange={handleCellChange}
                            />
                        )
                    }
                    default: {
                        return null
                    }
                }
            }, [
                value,
                inputType,
                field,
                fieldPointer,
                mode,
                config,
                props,
                fieldProps,
                handleBlur,
                handleChange,
                handleCellChange,
                record?.id
            ])}
        </StyleContent>
    )
}

export default WebsiteField
